import type {
  ApplyClickTrackingOptions,
  CampaignLinkRecord,
  EmailLinkElementType,
} from '../types/emailLinkAnalytics';

const ANCHOR_HREF_REGEX = /<a\s+([^>]*?)href=["']([^"']*?)["']([^>]*?)>/gi;

const MAX_DESIGN_PARSE_DEPTH = 4;

export function generateLinkId(): string {
  const suffix = Math.random().toString(36).slice(2, 10);
  return `lnk_${suffix}`;
}

export function isTrackableUrl(url: string): boolean {
  const trimmed = (url || '').trim();
  if (!trimmed || trimmed === '#') return false;
  const lower = trimmed.toLowerCase();
  if (lower.startsWith('mailto:') || lower.startsWith('tel:') || lower.startsWith('javascript:')) {
    return false;
  }
  if (lower.includes('unsubscribe') || lower.includes('recordpollresponse')) return false;
  if (lower.includes('/api/unsubscribe') || lower.includes('unsubscribe-one-click')) return false;
  if (lower.includes('/api/track/click') || lower.includes('/track/click')) return false;
  return true;
}

/** Unwrap click-tracking redirect URLs to the original destination for display/registry. */
export function resolveLinkDestinationUrl(rawHref: string): string {
  const trimmed = (rawHref || '').trim();
  if (!trimmed) return '';
  try {
    if (trimmed.includes('/api/track/click')) {
      const parsed = trimmed.startsWith('http')
        ? new URL(trimmed)
        : new URL(trimmed, 'https://app.scubamail.io');
      const dest = parsed.searchParams.get('url');
      if (dest) return decodeURIComponent(dest);
    }
  } catch {
    // keep raw href
  }
  return trimmed;
}

/** Normalize URLs for matching registry ↔ HTML ↔ link_stats (ignores protocol, query, hash). */
export function normalizeLinkUrlForMatch(url: string): string {
  const resolved = resolveLinkDestinationUrl((url || '').trim());
  if (!resolved) return '';
  try {
    const parsed = new URL(resolved);
    const host = parsed.hostname.toLowerCase().replace(/^www\./, '');
    const path = parsed.pathname.replace(/\/$/, '') || '';
    return `${host}${path}`;
  } catch {
    return resolved
      .toLowerCase()
      .replace(/^https?:\/\//, '')
      .replace(/\?.*$/, '')
      .replace(/#.*$/, '')
      .replace(/\/$/, '');
  }
}

function normalizeDesignJsonInput(designJson: unknown): unknown {
  if (!designJson) return null;
  if (typeof designJson === 'string') {
    try {
      return JSON.parse(designJson);
    } catch {
      return null;
    }
  }
  return designJson;
}

function extractBlocksFromDesignJson(designJson: unknown, depth = 0): Array<Record<string, unknown>> | null {
  const normalized = depth === 0 ? normalizeDesignJsonInput(designJson) : designJson;
  if (!normalized || typeof normalized !== 'object' || depth > MAX_DESIGN_PARSE_DEPTH) {
    return null;
  }
  const obj = normalized as Record<string, unknown>;
  if (Array.isArray(obj.blocks)) {
    return obj.blocks as Array<Record<string, unknown>>;
  }
  const nestedContent = obj.content;
  if (nestedContent && typeof nestedContent === 'object' && Array.isArray((nestedContent as Record<string, unknown>).blocks)) {
    return (nestedContent as Record<string, unknown>).blocks as Array<Record<string, unknown>>;
  }
  if (obj.designJson) {
    return extractBlocksFromDesignJson(obj.designJson, depth + 1);
  }
  if (obj.state) {
    return extractBlocksFromDesignJson(obj.state, depth + 1);
  }
  return null;
}

function buildActionHref(linkType?: string, linkUrl?: string): string {
  if (linkType === 'none') return '';
  const trimmed = (linkUrl || '').trim();
  if (!trimmed) return '';
  if (linkType === 'mailto') {
    return trimmed.toLowerCase().startsWith('mailto:') ? trimmed : `mailto:${trimmed}`;
  }
  if (linkType === 'tel') {
    const normalized = trimmed.replace(/\s+/g, '');
    return normalized.toLowerCase().startsWith('tel:') ? normalized : `tel:${normalized}`;
  }
  return trimmed;
}

function extractAnchorsFromHtmlFragment(html: string): Array<{ url: string; label: string }> {
  const results: Array<{ url: string; label: string }> = [];
  if (!html) return results;
  const regex = /<a\s+[^>]*?href=["']([^"']*?)["'][^>]*>([\s\S]*?)<\/a>/gi;
  let match: RegExpExecArray | null;
  while ((match = regex.exec(html)) !== null) {
    const url = match[1];
    if (!isTrackableUrl(url)) continue;
    const inner = match[2].replace(/<[^>]+>/g, '').trim();
    results.push({ url, label: inner || url });
  }
  return results;
}

function pushLink(
  links: CampaignLinkRecord[],
  params: {
    workspaceId: string;
    campaignId: string;
    campaignName?: string;
    blockId?: string;
    elementType: EmailLinkElementType;
    label: string;
    destinationUrl: string;
  }
): void {
  const destinationUrl = (params.destinationUrl || '').trim();
  if (!destinationUrl || !isTrackableUrl(destinationUrl)) return;

  links.push({
    linkId: generateLinkId(),
    workspaceId: params.workspaceId,
    campaignId: params.campaignId,
    sourceType: 'campaign',
    blockId: params.blockId,
    elementType: params.elementType,
    label: params.label || destinationUrl,
    destinationUrl,
    campaignName: params.campaignName,
    sortOrder: links.length,
  });
}

function walkBlocks(
  blocks: Array<Record<string, unknown>>,
  ctx: { workspaceId: string; campaignId: string; campaignName?: string },
  out: CampaignLinkRecord[]
): void {
  for (const block of blocks) {
    const blockId = typeof block.id === 'string' ? block.id : undefined;
    const type = typeof block.type === 'string' ? block.type : 'other';
    const content = (block.content && typeof block.content === 'object'
      ? block.content
      : {}) as Record<string, unknown>;

    switch (type) {
      case 'button': {
        const actionType = (content.actionType as string) || 'open-url';
        const rawHref =
          actionType === 'download-file'
            ? String(content.downloadFileUrl || '')
            : buildActionHref(content.actionType as string, content.url as string) ||
              buildActionHref(content.linkType as string, content.linkUrl as string) ||
              String(content.url || content.linkUrl || content.href || '');
        const href = resolveLinkDestinationUrl(rawHref);
        pushLink(out, {
          ...ctx,
          blockId,
          elementType: 'button',
          label: String(content.text || content.buttonText || 'Button'),
          destinationUrl: href,
        });
        break;
      }
      case 'hero': {
        const heroUrl = String(content.buttonUrl || content.url || '').trim();
        if (heroUrl) {
          pushLink(out, {
            ...ctx,
            blockId,
            elementType: 'hero',
            label: String(content.buttonText || content.title || 'Hero button'),
            destinationUrl: heroUrl,
          });
        }
        break;
      }
      case 'image': {
        if (content.linkUrl && String(content.linkUrl).trim()) {
          pushLink(out, {
            ...ctx,
            blockId,
            elementType: 'image',
            label: String(content.alt || 'Image link'),
            destinationUrl: String(content.linkUrl),
          });
        }
        break;
      }
      case 'logo': {
        const href = buildActionHref(content.linkType as string, content.linkUrl as string);
        if (href) {
          pushLink(out, {
            ...ctx,
            blockId,
            elementType: 'logo',
            label: 'Logo',
            destinationUrl: href,
          });
        }
        break;
      }
      case 'text':
      case 'paragraph': {
        const html = String(content.html || content.text || '');
        for (const anchor of extractAnchorsFromHtmlFragment(html)) {
          pushLink(out, {
            ...ctx,
            blockId,
            elementType: 'text_link',
            label: anchor.label,
            destinationUrl: anchor.url,
          });
        }
        break;
      }
      case 'social': {
        const platforms = (content.platforms || content.links || []) as Array<Record<string, unknown>>;
        for (const platform of platforms) {
          const url = String(platform.url || '');
          pushLink(out, {
            ...ctx,
            blockId,
            elementType: 'social',
            label: String(platform.name || platform.platform || 'Social'),
            destinationUrl: url,
          });
        }
        break;
      }
      case 'menu': {
        const items = (content.links || content.items || []) as Array<Record<string, unknown>>;
        for (const item of items) {
          pushLink(out, {
            ...ctx,
            blockId,
            elementType: 'menu',
            label: String(item.text || item.label || 'Menu link'),
            destinationUrl: String(item.url || '#'),
          });
        }
        break;
      }
      case 'navigation': {
        const navLinks = (content.links || []) as Array<Record<string, unknown>>;
        for (const link of navLinks) {
          pushLink(out, {
            ...ctx,
            blockId,
            elementType: 'navigation',
            label: String(link.text || link.label || 'Nav link'),
            destinationUrl: String(link.url || '#'),
          });
        }
        const actionUrl = String(content.actionUrl || content.ctaUrl || '');
        if (actionUrl) {
          pushLink(out, {
            ...ctx,
            blockId,
            elementType: 'navigation',
            label: String(content.actionText || 'CTA'),
            destinationUrl: actionUrl,
          });
        }
        break;
      }
      case 'product': {
        const products = (content.products || []) as Array<Record<string, unknown>>;
        for (const product of products) {
          pushLink(out, {
            ...ctx,
            blockId,
            elementType: 'product',
            label: String(product.title || product.name || 'Product'),
            destinationUrl: String(product.url || '#'),
          });
        }
        break;
      }
      case 'video': {
        const videoHref = String(content.actionUrl || content.linkUrl || content.url || '');
        if (videoHref) {
          pushLink(out, {
            ...ctx,
            blockId,
            elementType: 'video',
            label: String(content.title || 'Video'),
            destinationUrl: videoHref,
          });
        }
        break;
      }
      case 'footer': {
        const footerLinks = (content.links || []) as Array<Record<string, unknown>>;
        for (const link of footerLinks) {
          pushLink(out, {
            ...ctx,
            blockId,
            elementType: 'footer',
            label: String(link.text || 'Footer link'),
            destinationUrl: String(link.url || '#'),
          });
        }
        if (content.appStoreUrl) {
          pushLink(out, {
            ...ctx,
            blockId,
            elementType: 'footer',
            label: 'App Store',
            destinationUrl: String(content.appStoreUrl),
          });
        }
        if (content.googlePlayUrl) {
          pushLink(out, {
            ...ctx,
            blockId,
            elementType: 'footer',
            label: 'Google Play',
            destinationUrl: String(content.googlePlayUrl),
          });
        }
        break;
      }
      case 'poll': {
        const options = (content.options || []) as Array<Record<string, unknown>>;
        options.forEach((option, index) => {
          const redirect = String(option.url || '');
          if (redirect) {
            pushLink(out, {
              ...ctx,
              blockId,
              elementType: 'poll',
              label: String(option.label || `Poll option ${index + 1}`),
              destinationUrl: redirect,
            });
          }
        });
        break;
      }
      case 'columns': {
        const columnContent = content.columnContent as Array<Array<Record<string, unknown>>> | undefined;
        if (Array.isArray(columnContent)) {
          for (const column of columnContent) {
            if (Array.isArray(column)) {
              walkBlocks(column, ctx, out);
            }
          }
        }
        break;
      }
      case 'header': {
        const navLinks = (content.links || []) as Array<Record<string, unknown>>;
        for (const link of navLinks) {
          pushLink(out, {
            ...ctx,
            blockId,
            elementType: 'navigation',
            label: String(link.label || link.text || 'Nav link'),
            destinationUrl: resolveLinkDestinationUrl(String(link.url || '#')),
          });
        }
        const actionUrl = String(content.actionUrl || '');
        if (actionUrl) {
          pushLink(out, {
            ...ctx,
            blockId,
            elementType: 'button',
            label: String(content.actionText || 'CTA'),
            destinationUrl: resolveLinkDestinationUrl(actionUrl),
          });
        }
        break;
      }
      default: {
        const nestedBlocks = content.blocks || content.children;
        if (Array.isArray(nestedBlocks)) {
          walkBlocks(nestedBlocks as Array<Record<string, unknown>>, ctx, out);
        }
        break;
      }
    }
  }
}

function dedupeRegistryLinks(links: CampaignLinkRecord[]): CampaignLinkRecord[] {
  const seen = new Set<string>();
  const out: CampaignLinkRecord[] = [];
  for (const link of links) {
    const key = `${link.blockId || ''}:${link.destinationUrl}:${link.elementType}`;
    if (seen.has(key)) continue;
    seen.add(key);
    out.push({ ...link, sortOrder: out.length });
  }
  return out;
}

/** Extract trackable links from final HTML (fallback when design JSON is missing). */
export function extractLinksFromHtml(
  html: string,
  ctx: { workspaceId: string; campaignId: string; campaignName?: string }
): CampaignLinkRecord[] {
  const links: CampaignLinkRecord[] = [];
  if (!html) return links;

  const regex = /<a\s+[^>]*?href=["']([^"']*?)["'][^>]*>([\s\S]*?)<\/a>/gi;
  let match: RegExpExecArray | null;
  while ((match = regex.exec(html)) !== null) {
    const url = resolveLinkDestinationUrl(match[1]);
    if (!isTrackableUrl(url)) continue;
    const inner = match[2].replace(/<[^>]+>/g, '').trim();
    pushLink(links, {
      ...ctx,
      elementType: 'other',
      label: inner || url,
      destinationUrl: url,
    });
  }
  return links;
}

export function buildCampaignLinkRegistry(params: {
  designJson?: unknown;
  htmlContent?: string;
  workspaceId: string;
  campaignId: string;
  campaignName?: string;
}): CampaignLinkRecord[] {
  const ctx = {
    workspaceId: params.workspaceId,
    campaignId: params.campaignId,
    campaignName: params.campaignName,
  };

  const fromDesign: CampaignLinkRecord[] = [];
  const blocks = params.designJson ? extractBlocksFromDesignJson(params.designJson) : null;
  if (blocks?.length) {
    walkBlocks(blocks, ctx, fromDesign);
  }

  const fromHtml = extractLinksFromHtml(params.htmlContent || '', ctx);
  return dedupeRegistryLinks([...fromDesign, ...fromHtml]);
}

export function buildClickTrackingUrl(
  destinationUrl: string,
  linkId: string,
  options: Omit<ApplyClickTrackingOptions, 'linkRegistry'>,
  linkMeta?: Pick<CampaignLinkRecord, 'blockId' | 'elementType'>
): string {
  const origin = options.trackingOrigin.replace(/\/$/, '');
  const params = new URLSearchParams({
    campaign_id: options.campaignId,
    workspace_id: options.workspaceId,
    recipient_email: options.recipientEmail,
    link_id: linkId,
    url: destinationUrl,
    timestamp: Date.now().toString(),
  });
  if (options.recipientId) {
    params.set('recipient_id', options.recipientId);
  }
  if (linkMeta?.blockId) {
    params.set('block_id', linkMeta.blockId);
  }
  if (linkMeta?.elementType) {
    params.set('element_type', linkMeta.elementType);
  }
  return `${origin}/api/track/click?${params.toString()}`;
}

/**
 * Rewrites trackable anchor hrefs to click-tracking URLs.
 * Matches anchors to registry entries by order (same order as design JSON / HTML extraction).
 */
export function applyClickTrackingToHtml(
  html: string,
  options: ApplyClickTrackingOptions
): string {
  if (!html || !options.linkRegistry) return html;

  const registryByUrl = new Map<string, CampaignLinkRecord[]>();
  for (const entry of options.linkRegistry) {
    const key = normalizeLinkUrlForMatch(entry.destinationUrl);
    if (!key) continue;
    if (!registryByUrl.has(key)) registryByUrl.set(key, []);
    registryByUrl.get(key)!.push(entry);
  }
  const registryUseIndex = new Map<string, number>();

  return html.replace(ANCHOR_HREF_REGEX, (match, before, url, after) => {
    if (!isTrackableUrl(url)) {
      return match;
    }

    const urlKey = normalizeLinkUrlForMatch(url);
    const candidates = registryByUrl.get(urlKey) || [];
    const useIdx = registryUseIndex.get(urlKey) || 0;
    const registryEntry = candidates[useIdx] || candidates[0];
    if (candidates.length > 0) {
      registryUseIndex.set(urlKey, useIdx + 1);
    }
    const linkId = registryEntry?.linkId ?? generateLinkId();
    const trackingUrl = buildClickTrackingUrl(url, linkId, options, registryEntry);

    const beforeWithId = before.includes('data-sc-link-id')
      ? before
      : `${before}data-sc-link-id="${linkId}" `;
    const afterWithMeta = registryEntry
      ? `${after} data-sc-element-type="${registryEntry.elementType}"`
      : after;

    return `<a ${beforeWithId}href="${trackingUrl}"${afterWithMeta}>`;
  });
}
